import React, { useEffect, useState } from 'react';
import { Star, Quote } from 'lucide-react';

interface Review {
  author_name: string;
  rating: number;
  text: string;
  relative_time_description: string;
  profile_photo_url: string;
}

interface ReviewsData {
  success: boolean;
  name: string;
  rating: number;
  totalReviews: number;
  reviews: Review[];
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-5 h-5 ${
            star <= rating
              ? 'fill-yellow-400 text-yellow-400'
              : 'fill-gray-200 text-gray-200'
          }`}
        />
      ))}
    </div>
  );
}

function ReviewCard({ review }: { review: Review }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = review.text.length > 200;
  const displayText = expanded || !isLong ? review.text : review.text.slice(0, 200) + '...';

  return (
    <div className="bg-white rounded-xl shadow-md p-6 flex flex-col h-full">
      <div className="flex items-start gap-4 mb-4">
        <img
          src={review.profile_photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(review.author_name)}&background=1B4965&color=fff`}
          alt={review.author_name}
          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-[#1B4965] truncate">{review.author_name}</h4>
          <p className="text-sm text-gray-500">{review.relative_time_description}</p>
        </div>
      </div>

      <StarRating rating={review.rating} />

      <div className="mt-4 flex-1">
        <Quote className="w-6 h-6 text-[#FF6B35] opacity-50 mb-2" />
        <p className="text-gray-600 leading-relaxed">
          {displayText}
        </p>
        {isLong && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[#FF6B35] text-sm mt-2 hover:underline"
          >
            {expanded ? 'Weniger anzeigen' : 'Mehr lesen'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function GoogleReviews() {
  const [data, setData] = useState<ReviewsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchReviews() {
      try {
        const response = await fetch('/.netlify/functions/google-reviews');
        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error);
        }

        setData(result);
      } catch (err) {
        console.error('Error loading reviews:', err);
        setError('Bewertungen konnten nicht geladen werden');
      } finally {
        setLoading(false);
      }
    }

    fetchReviews();
  }, []);

  if (loading) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-64 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-48 mx-auto mb-12"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-xl p-6 h-64"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error || !data) {
    return null; // Silently fail - don't show broken section
  }

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="section-title">Das sagen unsere Kunden</h2>

          {/* Overall Rating */}
          <div className="flex items-center justify-center gap-3 mt-4">
            <div className="flex items-center gap-2">
              <span className="text-4xl font-bold text-[#1B4965]">{data.rating.toFixed(1)}</span>
              <div className="flex flex-col items-start">
                <StarRating rating={Math.round(data.rating)} />
                <span className="text-sm text-gray-500">{data.totalReviews} Bewertungen auf Google</span>
              </div>
            </div>
          </div>

          {/* Google Attribution */}
          <a
            href="https://maps.app.goo.gl/wV9m1opUu1KshFLZ6"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-4 text-sm text-gray-500 hover:text-[#FF6B35] transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
            Alle Bewertungen auf Google Maps ansehen
          </a>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.reviews.slice(0, 6).map((review, index) => (
            <ReviewCard key={index} review={review} />
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <a
            href="https://search.google.com/local/writereview?placeid=ChIJZ6Sy5hynl0cRZOQyi1I8icQ"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2"
          >
            <Star className="w-5 h-5" />
            Bewertung schreiben
          </a>
        </div>
      </div>
    </section>
  );
}
